import { adminOidcConfig } from '../lib/oidc/oidcConfig';
import { authServerErrorMessage, authServerUrl } from '../lib/oidc/oidcCore';
import type { CreationOptionsJSON, RequestOptionsJSON } from '../lib/oidc/passkeyCodec';
import { enrolmentTokenBody } from '../lib/oidc/passkeyFlow';

/**
 * RBAC Phase 3 — auth-server session endpoints used by the admin SPA's login page when it acts as
 * the `admin-console` client's login_url. All calls go through `VITE_AUTH_SERVER_BASE` with
 * `credentials: 'include'` so the auth-server session cookie (pending OAuth request, pending MFA)
 * is the one `/oauth/authorize` sees when the browser follows `redirectTo`.
 *
 * Passwords are sent once and never stored.
 */

export class AuthServerError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, fallback: string) {
    super(authServerErrorMessage(status, body, fallback));
    this.status = status;
    this.body = body;
  }
}

async function post<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const cfg = adminOidcConfig();
  let res: Response;
  try {
    res = await fetch(authServerUrl(cfg.authServerBase, path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new AuthServerError(0, null, 'Could not reach the sign-in service. Check your connection and try again.');
  }
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  if (!res.ok) throw new AuthServerError(res.status, json, fallback);
  return json as T;
}

export type AuthServerLoginResponse = {
  mfaRequired: boolean;
  mfaEnabled?: boolean;
  mfaVerified?: boolean;
  /** auth-server-relative pending authorize URL, or null (MFA pending / no pending OAuth request). */
  redirectTo: string | null;
  email?: string;
};

export type AuthServerRedirectResponse = { redirectTo: string | null };

export type AuthServerMfaSetupResponse = { secret: string; qrCode: string; otpAuthUrl: string };

export type AuthServerMfaEnableResponse = { recoveryCodes: string[]; redirectTo: string | null };

export const authServerAuthApi = {
  login(email: string, password: string): Promise<AuthServerLoginResponse> {
    return post('/auth/login', { email, password }, 'Sign-in failed. Try again.');
  },
  verifyTotp(code: string, rememberDevice = false): Promise<AuthServerRedirectResponse> {
    return post('/auth/mfa/verify', { code, rememberDevice }, 'Could not verify the authenticator code.');
  },
  verifyRecoveryCode(code: string): Promise<AuthServerRedirectResponse & { remainingRecoveryCodes?: number }> {
    return post('/auth/mfa/recovery', { code }, 'Could not verify the recovery code.');
  },
  setupMfa(): Promise<AuthServerMfaSetupResponse> {
    return post('/auth/mfa/setup', {}, 'Could not start authenticator setup.');
  },
  enableMfa(code: string): Promise<AuthServerMfaEnableResponse> {
    return post('/auth/mfa/enable', { code }, 'Could not enable the authenticator.');
  },
  // RBAC Phase 4: passkeys on the auth-server session (sign-in, acr_values=phr step-up, enrolment).
  passkeyLoginOptions(email: string): Promise<{ options: RequestOptionsJSON }> {
    return post('/auth/passkey/login/options', { email }, 'Could not start passkey sign-in.');
  },
  passkeyLoginVerify(email: string, response: unknown): Promise<AuthServerLoginResponse> {
    return post('/auth/passkey/login/verify', { email, response }, 'Passkey sign-in failed. Try again.');
  },
  passkeyStepUpOptions(): Promise<{ options: RequestOptionsJSON }> {
    return post('/auth/passkey/step-up/options', {}, 'Could not start passkey confirmation.');
  },
  passkeyStepUpVerify(response: unknown): Promise<AuthServerRedirectResponse> {
    return post('/auth/passkey/step-up/verify', { response }, 'Could not confirm with your passkey.');
  },
  /**
   * A staff account's FIRST passkey needs a one-time enrolment code from an IAM admin
   * (`enrolmentToken`; 403 ENROLMENT_TOKEN_REQUIRED without it). auth-server keeps the code it
   * validated here in the session, so sending it again at verify is optional.
   */
  passkeyRegisterOptions(enrolmentToken?: string): Promise<{ options: CreationOptionsJSON }> {
    return post('/auth/passkey/register/options', enrolmentTokenBody(enrolmentToken), 'Could not start passkey registration.');
  },
  passkeyRegisterVerify(response: unknown, deviceName?: string, enrolmentToken?: string): Promise<unknown> {
    return post(
      '/auth/passkey/register/verify',
      { response, ...(deviceName ? { deviceName } : {}), ...enrolmentTokenBody(enrolmentToken) },
      'Could not register the passkey.',
    );
  },
};

/** 403 bodies from `/auth/login` that need a different path (email verification / reset). */
export function loginBlockedReason(err: unknown): 'email-verification' | 'must-reset' | 'disabled' | null {
  if (!(err instanceof AuthServerError) || err.status !== 403) return null;
  const b = (err.body && typeof err.body === 'object' ? err.body : {}) as Record<string, unknown>;
  if (b.requiresEmailVerification === true) return 'email-verification';
  const code = typeof b.code === 'string' ? b.code : '';
  if (/RESET/i.test(code) || b.mustResetPassword === true) return 'must-reset';
  if (/DISABLED/i.test(code)) return 'disabled';
  return null;
}
